import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import Icon from "@mdi/react";
import {
  mdiArrowLeft,
  mdiLightningBolt,
  mdiInformationOutline,
  mdiPlus,
} from "@mdi/js";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $createParagraphNode,
  $createTextNode,
  KEY_ENTER_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from "lexical";
import { useApp } from "app/contexts/AppContext";
import PageLayout from "app/components/common/PageLayout";
import { PartialLoadingSpinner } from "app/components/LoadingSpinner";
import CustomToast from "app/components/common/CustomToast";
import DiscardChangesModal from "app/components/common/DiscardChangesModal";
import "app/components/common/HowItWorksModal/howItWorksModal.css";
import { VariableNode, $createVariableNode } from "./VariableNode";
import {
  fetchEmailTemplates,
  fetchSenders,
  fetchTemplateVariables,
  fetchTemplatePreview,
  refreshTemplatePreview,
  saveEmailTemplate,
  clearEditState,
  clearSaveStatus,
} from "../slice";
import LexicalEditor from "./LexicalEditor";
import PreviewPanel from "./PreviewPanel";
import {
  TRIGGER_CONTEXT,
  TRIGGER_CONTEXT_BY_EVENT,
  TRIGGER_DESCRIPTION,
} from "./triggerCopy";
import "./editTemplate.css";

const TRIGGER_UNIT_OPTIONS = [
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
];

const SubjectEditor = ({ initialValue, onChange, inputRef, variables }) => {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);

  const variableLookup = useMemo(() => {
    const lookup = {};
    if (variables) {
      for (const v of variables) {
        lookup[v.name] = v.display_name || v.name;
      }
    }
    return lookup;
  }, [variables]);

  const config = useMemo(
    () => ({
      namespace: "SubjectEditor",
      theme: {
        paragraph: "subject-editor-paragraph",
      },
      nodes: [VariableNode],
      onError: (error) => {
        console.error("Subject editor error:", error);
      },
    }),
    []
  );

  return (
    <LexicalComposer initialConfig={config}>
      <SubjectEditorPlugins
        initialValue={initialValue}
        onChange={onChange}
        inputRef={inputRef}
        variableLookup={variableLookup}
        editorRef={editorRef}
        isInitialized={isInitialized}
      />
    </LexicalComposer>
  );
};

const SubjectEditorPlugins = ({
  initialValue,
  onChange,
  inputRef,
  variableLookup,
  editorRef,
  isInitialized,
}) => {
  const [editor] = useLexicalComposerContext();
  const isFirstChange = useRef(true);

  // Initialize content
  useEffect(() => {
    if (isInitialized.current || !initialValue) return;
    isInitialized.current = true;

    editor.update(() => {
      const root = $getRoot();
      root.clear();
      const paragraph = $createParagraphNode();

      const text = initialValue;
      let lastIndex = 0;
      let match;
      const regex = /\{\{([^}]+)\}\}/g;

      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) {
          paragraph.append($createTextNode(text.slice(lastIndex, match.index)));
        }
        const varName = match[1].trim();
        const displayName = variableLookup[varName] || varName;
        paragraph.append($createVariableNode(varName, displayName));
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < text.length) {
        paragraph.append($createTextNode(text.slice(lastIndex)));
      }

      root.append(paragraph);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // onChange handler
  const handleChange = useCallback(
    (editorState) => {
      if (isFirstChange.current) {
        isFirstChange.current = false;
        return;
      }
      editorState.read(() => {
        const root = $getRoot();
        onChange(root.getTextContent());
      });
    },
    [onChange]
  );

  // Block Enter key (single line)
  useEffect(() => {
    return editor.registerCommand(
      KEY_ENTER_COMMAND,
      () => true,
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  // Expose insertVariable via ref
  useEffect(() => {
    if (inputRef) {
      inputRef.current = {
        insertVariable: (varText) => {
          const varName = varText.slice(2, -2);
          const displayName = variableLookup[varName] || varName;
          editor.update(() => {
            const selection = $getSelection();
            if ($isRangeSelection(selection)) {
              const node = $createVariableNode(varName, displayName);
              selection.insertNodes([node]);
            } else {
              // No selection — append to end
              const root = $getRoot();
              const lastParagraph = root.getLastChild();
              if (lastParagraph) {
                lastParagraph.append($createVariableNode(varName, displayName));
              }
            }
          });
          editor.focus();
        },
      };
    }
  });

  return (
    <div className="subject-editor-wrapper">
      <RichTextPlugin
        contentEditable={
          <ContentEditable className="subject-editor-content" ref={editorRef} />
        }
        placeholder={
          <div className="subject-editor-placeholder">Subject</div>
        }
        ErrorBoundary={LexicalErrorBoundary}
      />
      <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
      <HistoryPlugin />
    </div>
  );
};

const InsertVariableButton = ({ variables, onInsert }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="variable-dropdown-wrapper" ref={wrapperRef}>
      <Button
        variant="outline-primary"
        size="sm"
        className="insert-variable-btn"
        onClick={() => setShowDropdown((prev) => !prev)}
      >
        <Icon path={mdiPlus} size={0.67} />
        Insert Variable
      </Button>
      {showDropdown && (
        <div className="variable-dropdown">
          {(() => {
            const grouped = {};
            for (const v of variables) {
              const cat = v.category || "Other";
              if (!grouped[cat]) {
                grouped[cat] = [];
              }
              grouped[cat].push(v);
            }
            const categories = Object.keys(grouped);
            return categories.map((cat, catIdx) => (
              <div key={cat}>
                {catIdx > 0 && <div className="variable-dropdown-divider" />}
                <div className="variable-dropdown-category">{cat}</div>
                {grouped[cat].map((v) => (
                  <div
                    key={v.name}
                    className="variable-dropdown-item"
                    onClick={() => {
                      onInsert(v);
                      setShowDropdown(false);
                    }}
                  >
                    {v.display_name || v.name}
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>
      )}
    </div>
  );
};

const EditTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const subjectRef = useRef(null);
  const {
    device: { isMobile },
  } = useApp();
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showReportLinkModal, setShowReportLinkModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const isDirtyRef = useRef(false);

  const {
    sender,
    senders,
    senderProviderId,
    sendersFetchStatus,
    templates,
    templatesFetchStatus,
    variables,
    preview,
    previewFetchStatus,
    refreshPreviewStatus,
    saveStatus,
    saveError,
  } = useSelector((state) => state.emailAutomation);

  const template = templates.find((t) => t.id === id);

  const [formData, setFormData] = useState(null);
  const [charCount, setCharCount] = useState(0);

  // Fetch templates if not loaded
  useEffect(() => {
    if (templatesFetchStatus === "idle") {
      dispatch(fetchEmailTemplates());
    }
  }, [dispatch, templatesFetchStatus]);

  // Fetch senders for preview header
  useEffect(() => {
    if (sendersFetchStatus === "idle") {
      dispatch(fetchSenders());
    }
  }, [dispatch, sendersFetchStatus]);

  // Once template is available, init form and fetch variables + preview
  useEffect(() => {
    if (template && !formData) {
      setFormData({
        is_active: template.is_active,
        subject: template.subject || "",
        body: template.body || "",
        trigger_value: template.trigger_value || "",
        trigger_unit: template.trigger_unit || "hours",
        cc_agent: template.cc_agent || false,
      });
      setCharCount((template.body || "").length);
      dispatch(fetchTemplateVariables(template.event_type));
      dispatch(fetchTemplatePreview(id));
    }
  }, [template, formData, dispatch, id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearEditState());
    };
  }, [dispatch]);

  // Browser tab close / refresh warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Intercept sidebar / header link clicks
  useEffect(() => {
    const handleClick = (e) => {
      if (!isDirtyRef.current) return;

      const link = e.target.closest("a[href]");
      if (!link) return;

      const href = link.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("http")) return;

      e.preventDefault();
      e.stopPropagation();
      setPendingNavigation(href);
      setShowDiscardModal(true);
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  // Handle save success/failure
  useEffect(() => {
    if (saveStatus === "success") {
      isDirtyRef.current = false;
      setToastMessage("Changes Saved");
      setShowSuccessToast(true);
      dispatch(clearSaveStatus());
    } else if (saveStatus === "failed") {
      setToastMessage(saveError || "Failed to save. Please try again.");
      setShowErrorToast(true);
      dispatch(clearSaveStatus());
    }
  }, [saveStatus, saveError, dispatch, navigate]);

  const updateField = useCallback((field, value) => {
    isDirtyRef.current = true;
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleBodyChange = useCallback((html) => {
    isDirtyRef.current = true;
    setFormData((prev) => (prev ? { ...prev, body: html } : prev));
  }, []);

  // Sync the editor's normalized HTML on init without marking the form
  // dirty, so an unedited body is saved as block-delimited HTML (preserving
  // line breaks in the sent email) instead of the raw plain-text default.
  const handleBodyInit = useCallback((html) => {
    setFormData((prev) => (prev ? { ...prev, body: html } : prev));
  }, []);

  const handleBodyTextChange = useCallback((text) => {
    setCharCount(text.length);
  }, []);

  const handleSubjectInsertVariable = useCallback((variable) => {
    const varText = `{{${variable.name}}}`;
    if (subjectRef.current?.insertVariable) {
      subjectRef.current.insertVariable(varText);
    } else {
      setFormData((prev) => ({
        ...prev,
        subject: (prev.subject || "") + varText,
      }));
    }
  }, []);

  const showTriggerConfig =
    template?.trigger_type === "time_before" ||
    template?.trigger_type === "time_after";

  const handleSave = () => {
    // Report Delivery must include {{Report Link}} variable
    if (
      template?.event_type === "report_delivery" &&
      formData.body &&
      !formData.body.includes("{{Report Link}}")
    ) {
      setShowReportLinkModal(true);
      return;
    }

    if (charCount > 2000) {
      setToastMessage("Email body must not exceed 2,000 characters.");
      setShowErrorToast(true);
      return;
    }

    if (showTriggerConfig) {
      const val = Number(formData.trigger_value);
      if (!val || val < 1) {
        setToastMessage("Trigger value must be at least 1.");
        setShowErrorToast(true);
        return;
      }
    }

    const data = {
      is_active: formData.is_active,
      subject: formData.subject,
      body: formData.body,
      cc_agent: formData.cc_agent,
    };

    if (showTriggerConfig) {
      data.trigger_value = Number(formData.trigger_value);
      data.trigger_unit = formData.trigger_unit;
    }

    dispatch(saveEmailTemplate({ templateId: id, data }));
  };

  const handleRefreshPreview = () => {
    dispatch(
      refreshTemplatePreview({
        templateId: id,
        subject: formData.subject,
        body: formData.body,
      })
    );
  };

  const handleCancel = () => {
    if (isDirtyRef.current) {
      setPendingNavigation("/email-automation");
      setShowDiscardModal(true);
    } else {
      navigate("/email-automation");
    }
  };

  // Loading state
  if (!template || !formData) {
    return (
      <PageLayout className="edit-template-container">
        <PartialLoadingSpinner />
      </PageLayout>
    );
  }

  return (
    <PageLayout
      customHeader={
        <>
          <button
            className="edit-template-back-link"
            onClick={handleCancel}
          >
            <Icon path={mdiArrowLeft} size={0.67} />
            Back to Email Automation
          </button>
          <div className="edit-template-header">
            <div className="edit-template-header-left">
              <h2 className="edit-template-name">{template.name}</h2>
              <div className="email-card-status">
                <span
                  className={`status-dot ${template.is_active ? "active" : "inactive"}`}
                />
                <span className="status-text">
                  {template.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
            <div className="edit-template-header-actions">
              <Button variant="outline-secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saveStatus === "loading"}
              >
                {saveStatus === "loading" ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </div>
          <p className="edit-template-description">{template.description}</p>
        </>
      }
      className="edit-template-container"
    >

      {/* Two-column Layout */}
      <div className="edit-template-layout">
        {/* Left Column */}
        <div className="edit-template-left-column">
          {/* Automation Status */}
          <div className="edit-template-section">
            <h4 className="edit-template-section-title">Automation Status</h4>
            <div className="status-radio-group">
              <div className="status-radio-option">
                <Form.Check
                  type="radio"
                  name="automationStatus"
                  id="status-active"
                  label="Active"
                  checked={formData.is_active}
                  onChange={() => updateField("is_active", true)}
                />
                <span className="status-radio-description">
                  This email will be sent based on its sending rules.
                </span>
              </div>
              <div className="status-radio-option">
                <Form.Check
                  type="radio"
                  name="automationStatus"
                  id="status-inactive"
                  label="Inactive"
                  checked={!formData.is_active}
                  onChange={() => updateField("is_active", false)}
                />
                <span className="status-radio-description">
                  No emails will be sent to the client.
                </span>
              </div>
            </div>
          </div>

          {/* Sending Rules */}
          <div className="edit-template-section">
            <h4 className="edit-template-section-title">Sending Rules</h4>
            <div className="sending-rules-content">
              <div className="trigger-row">
                <div className="trigger-icon">
                  <Icon path={mdiLightningBolt} size={0.67} />
                </div>
                <div className="trigger-config">
                  <span className="trigger-label">Trigger</span>
                  {showTriggerConfig ? (
                    <div className="trigger-inputs">
                      <span className="trigger-context-text">Send</span>
                      <Form.Control
                        type="number"
                        value={formData.trigger_value}
                        onChange={(e) =>
                          updateField("trigger_value", e.target.value)
                        }
                        min={1}
                      />
                      <Form.Select
                        value={formData.trigger_unit}
                        onChange={(e) =>
                          updateField("trigger_unit", e.target.value)
                        }
                      >
                        {TRIGGER_UNIT_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Form.Select>
                      <span className="trigger-context-text">
                        {TRIGGER_CONTEXT_BY_EVENT[template.event_type] ||
                          TRIGGER_CONTEXT[template.trigger_type] ||
                          ""}
                      </span>
                    </div>
                  ) : (
                    <span className="trigger-description-text">
                      {TRIGGER_DESCRIPTION[template.trigger_type]?.[
                        template.event_type
                      ] ||
                        template.trigger_display ||
                        ""}
                    </span>
                  )}
                </div>
              </div>
              <hr className="sending-rules-divider" />
              <div className="cc-agent-option">
                <Form.Check
                  type="checkbox"
                  id="cc-agent"
                  label="CC Agent (if available)"
                  checked={formData.cc_agent}
                  onChange={(e) => updateField("cc_agent", e.target.checked)}
                />
                <span className="cc-agent-description">
                  If agent email is not provided, CC will be skipped.
                </span>
              </div>
            </div>
          </div>

          {/* Email Content */}
          <div className="edit-template-section">
            <h4 className="edit-template-section-title">Email Content</h4>

            {/* Subject */}
            <div className="email-content-field">
              <div className="field-header">
                <span className="field-label">Subject</span>
                <InsertVariableButton
                  variables={variables}
                  onInsert={handleSubjectInsertVariable}
                />
              </div>
              <SubjectEditor
                initialValue={template.subject}
                onChange={(val) => updateField("subject", val)}
                inputRef={subjectRef}
                variables={variables}
              />
            </div>

            {/* Body */}
            <div className="email-content-field">
              <div className="field-header">
                <span className="field-label">Body</span>
                <InsertVariableButton
                  variables={variables}
                  onInsert={(v) => {
                    // Insert variable into Lexical editor via custom event
                    window.dispatchEvent(
                      new CustomEvent("insert-email-variable", {
                        detail: v,
                      })
                    );
                  }}
                />
              </div>
              <LexicalEditor
                initialContent={template.body}
                variables={variables}
                onChange={handleBodyChange}
                onInit={handleBodyInit}
                onTextChange={handleBodyTextChange}
              />
              <p className={`char-count ${charCount > 2000 ? "over-limit" : ""}`}>
                {charCount}/2,000
              </p>
              <div className="editor-info-text">
                <Icon path={mdiInformationOutline} size={0.67} />
                <span>
                  Inspection Details will be added to the end of this email.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <PreviewPanel
          preview={preview}
          previewFetchStatus={previewFetchStatus}
          refreshPreviewStatus={refreshPreviewStatus}
          onRefresh={handleRefreshPreview}
          senderInfo={(() => {
            const arr = Array.isArray(senders) ? senders : [];
            const preferred =
              arr.find((s) => s.id === senderProviderId) ||
              arr.find((s) => s.is_preferred);
            return preferred
              ? { name: preferred.name, email: preferred.email }
              : sender;
          })()}
          templateInfo={formData}
        />
      </div>

      {/* Mobile bottom actions */}
      <div className="edit-template-mobile-actions">
        <Button variant="outline-secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saveStatus === "loading"}
        >
          {saveStatus === "loading" ? (
            <Spinner size="sm" animation="border" />
          ) : (
            "Save"
          )}
        </Button>
      </div>

      <CustomToast
        show={showSuccessToast}
        onClose={() => setShowSuccessToast(false)}
        message={toastMessage}
        variant="success"
        position="bottom-end"
        isMobile={isMobile}
        autohide
        delay={4000}
      />

      <CustomToast
        show={showErrorToast}
        onClose={() => setShowErrorToast(false)}
        message={toastMessage}
        variant="error"
        position="bottom-end"
        isMobile={isMobile}
        autohide
        delay={5000}
      />

      <Modal
        show={showReportLinkModal}
        onHide={() => setShowReportLinkModal(false)}
        centered
        className="how-it-works-modal"
      >
        <Modal.Body className="pt-0">
          <Modal.Header className="modal-title-with-border" closeButton>
            <Modal.Title>Add Report Link Variable</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-1 px-2">
            <p className="mb-2">
              This email needs the {"{{Report Link}}"} variable so clients can
              access their reports.
            </p>
            <p className="mb-0">
              Use &quot;Insert Variable&quot; to add it to your email body.
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="primary"
              onClick={() => setShowReportLinkModal(false)}
            >
              Back to Editor
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

      <DiscardChangesModal
        show={showDiscardModal}
        onHide={() => {
          setShowDiscardModal(false);
          setPendingNavigation(null);
        }}
        onConfirm={() => {
          isDirtyRef.current = false;
          setShowDiscardModal(false);
          if (pendingNavigation) {
            navigate(pendingNavigation);
          }
          setPendingNavigation(null);
        }}
      />
    </PageLayout>
  );
};

export default EditTemplate;
